import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Divider, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGridPro } from '@mui/x-data-grid-pro';
import QuantityEditor from './QuantityEditor';
import { toast } from 'react-toastify';

// Status ids that control which quantities can be edited and by whom.
const EDITABLE_STATUS_ID = 1; // PM can edit requested quantities
const CONFIRMABLE_STATUS_IDS = [2, 3]; // Warehouse can edit confirmed quantities
const AUTOSAVE_DELAY_MS = 2000;

// Build the avatar initials from a product name, e.g. "Acme Tablet" -> "AT".
const getInitials = (name) =>
  (name ?? '')
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();

export default function PlanItemsGrid({
  form,
  setForm,
  type,
  handleSavePartial = null,
  staff = null,
}) {
  const isEditable = form.status_id === EDITABLE_STATUS_ID;
  const saveTimeout = useRef(null);
  const products = useMemo(() => form?.products ?? [], [form?.products]);

  // Cancel any pending autosave when the component unmounts so we don't
  // call handleSavePartial after the grid is gone.
  useEffect(() => {
    return () => clearTimeout(saveTimeout.current);
  }, []);

  const onRemoveProduct = (id) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.filter((item) => item.id !== id),
    }));
  };

  const updateRequested = (id, value) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.map((row) =>
        row.id === id ? { ...row, requested_qty: value } : row,
      ),
    }));
  };

  const updateConfirmed = (id, value, requested_qty) => {
    if (staff === null || staff === '') {
      toast.warning('Assigned to is required');
      return;
    }

    if (value > requested_qty) {
      toast.warning('Confirmed quantity is more than the requested quantity');
      return;
    }

    setForm((prev) => {
      const updatedForm = {
        ...prev,
        products: prev.products.map((row) =>
          row.id === id ? { ...row, confirmed_qty: value } : row,
        ),
      };

      clearTimeout(saveTimeout.current);

      saveTimeout.current = setTimeout(() => {
        handleSavePartial?.(updatedForm, id);
      }, AUTOSAVE_DELAY_MS);

      return updatedForm;
    });
  };

  const totalRequested = useMemo(
    () => products.reduce((total, item) => total + Number(item?.requested_qty || 0), 0),
    [products],
  );

  const totalConfirmed = useMemo(
    () => products.reduce((total, item) => total + Number(item?.confirmed_qty || 0), 0),
    [products],
  );

  const columns = [
    {
      field: 'product',
      headerName: 'PRODUCT',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: '#E8F5E9',
              color: '#2E7D32',
              fontWeight: 700,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mr: 2,
              flexShrink: 0,
            }}
          >
            {getInitials(row?.name)}
          </Box>

          <Box>
            <Typography variant='body2' fontWeight={600}>
              {row.name}
            </Typography>

            <Typography variant='caption' color='text.secondary'>
              {row.label}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'requested_qty',
      headerName: 'REQUESTED',
      width: 180,
      sortable: false,
      renderCell: ({ row }) =>
        isEditable && type === 'pm' ? (
          <QuantityEditor
            value={row.requested_qty}
            onChange={(value) => updateRequested(row.id, value)}
          />
        ) : (
          row.requested_qty ?? 0
        ),
    },
    {
      field: 'confirmed_qty',
      headerName: 'CONFIRMED',
      width: 180,
      sortable: false,
      renderCell: ({ row }) =>
        CONFIRMABLE_STATUS_IDS.includes(form.status_id) && type === 'wh' ? (
          <QuantityEditor
            value={row.confirmed_qty ?? 0}
            onChange={(value) => updateConfirmed(row.id, value, row.requested_qty)}
          />
        ) : (
          row.confirmed_qty ?? 0
        ),
    },

    ...(isEditable
      ? [
          {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => (
              <IconButton size='small' onClick={() => onRemoveProduct(row.id)}>
                <CloseIcon fontSize='small' />
              </IconButton>
            ),
          },
        ]
      : []),
  ];

  return (
    <Paper
      sx={{
        overflow: 'hidden',
      }}
    >
      {/* Header */}

      <Box sx={{ p: 2 }}>
        <Typography fontWeight={700}>Plan Items</Typography>

        <Typography variant='body2' color='text.secondary'>
          {products.length} item
          {products.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Divider />

      {/* Grid */}

      <DataGridPro
        rows={products}
        columns={columns}
        hideFooter
        disableRowSelectionOnClick
        rowHeight={72}
        getRowId={(row) => row.id}
        density='compact'
        sx={{
          border: 0,
          height: 400,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#fafafa',
          },

          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },

          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
          },
        }}
      />

      <Divider />

      {/* Totals */}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 56,
          px: 3,
          bgcolor: '#fafafa',
          fontWeight: 700,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700}>Totals</Typography>
        </Box>

        <Box
          sx={{
            width: 180,
            textAlign: 'center',
          }}
        >
          {totalRequested}
        </Box>

        <Box
          sx={{
            width: 180,
            textAlign: 'center',
            color: 'success.main',
          }}
        >
          {totalConfirmed}
        </Box>

        <Box width={60} />
      </Box>
    </Paper>
  );
}
